'use strict';

export default angular
  .module('maestro.services.data.billing', [
    'maestro.services.webcore'
  ])
  .service('BillingService', BillingService)
  .name;

function BillingService(webcore, $q, CacheService) {

  // #region Billing Plans

  /**
   * Get billing items.
   * @returns {Promise}
   */
  this.getBillingItems = CacheService.cachePromiseFunc(() => {
    return $q.when(
      webcore.services.billing.getBillingItems()
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  });

  /**
   * Get available billing plans.
   * @returns {Promise}
   */
  this.getBillingPlans = CacheService.cachePromiseFunc(() => {
    return $q.when(
      webcore.services.billing.getBillingPlans()
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  });

  /**
   * Create or update billing plan.
   * @param billingPlan
   * @param {number} [planId]
   * @returns {Promise}
   */
  this.setBillingPlan = (billingPlan, planId) => {
    this.getBillingPlans.clearEntireCache();
    return $q.when(
      webcore.services.billing.setBillingPlan(billingPlan, planId)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  /**
   * Get billing plan versions.
   * @param {number} planId
   * @returns {Promise}
   */
  this.getBillingPlanVersions = (planId) => {
    return $q.when(
      webcore.services.billing.getBillingPlanVersions(planId)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  /**
   * Create or update billing plan version.
   * @param {number} planId
   * @param billingPlanVersion
   * @param {number} planVersionId
   * @returns
   */
  this.setBillingPlanVersion = (planId, billingPlanVersion, planVersionId) => {
    let params = {};
    if (planVersionId) {
      params.planVersionId = planVersionId;
    }

    return $q.when(
      webcore.services.billing.setBillingPlanVersion(planId, billingPlanVersion, params)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  // #endregion

  // #region Organization Billing Plans

  /**
   * Get organization billing plans.
   * @param {number} organizationId
   * @returns {Promise}
   */
  this.getOrganizationBillingPlans = CacheService.cachePromiseFunc((organizationId) => {
    return $q.when(
      webcore.services.billing.getOrganizationBillingPlans(organizationId)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  });

  /**
   * Set organization billing plan.
   * @param {number} organizationId
   * @param {number} planId
   * @param {string|number} [startDate]
   * @returns {Promise}
   */
  this.setOrganizationBillingPlan = (organizationId, planId, startDate) => {
    this.getOrganizationBillingPlans.clearEntireCache();
    let params = {
      plamId: planId,
    };

    if (startDate) {
      params.startDate = startDate;
    }

    return $q.when(
      webcore.services.billing.setOrganizationBillingPlan(organizationId, params)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  /**
   * Delete organization billing plan.
   * @param {number} organizationId
   * @param {string|number} [startDate]
   * @returns
   */
  this.deleteOrganizationBillingPlan = (organizationId, startDate) => {
    this.getOrganizationBillingPlans.clearEntireCache();
    return $q.when(
      webcore.services.billing.deleteOrganizationBillingPlan(organizationId, startDate)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  // #endregion

  // #region Organization Bills

  /**
   * Get organization bills.
   * @param {number} organizationId
   * @param {string|number} startDate
   * @param {string|number} [endDate]
   * @returns {Promise}
   */
  this.getBills = (organizationId, startDate, endDate) => {
    let params = {
      startDate: startDate,
      sortCollection: 'bills',
      sortBy: 'startDate',
      sortOrder: 'desc'
    };

    if (endDate) {
      params.endDate = endDate;
    }

    return $q.when(
      webcore.services.billing.getOrganizationBills(organizationId, params)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  /**
   * Generate organization bill.
   * @param {number} organizationId
   * @param {number|string} [date]
   * @returns {Promise}
   */
  this.generateBill = (organizationId, date) => {
    return $q.when(
      webcore.services.billing.generateOrganizationBill(organizationId, date)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  /**
   * Return an organization bill in PDF or CSV format.
   * @param {number} organizationId
   * @param {number} billId
   * @param {string} [format]
   * @returns {Promise}
   */
  this.getBillContent = (organizationId, billId, format) => {
    let fileFormat = format || 'pdf';
    return $q.when(
      webcore.services.billing.getBillContent(organizationId, billId, fileFormat)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  // #endregion

}
